import {
    Check,
    ChevronDown,
} from "lucide-react";
import {
    useEffect,
    useRef,
    useState,
} from "react";

import {
    PersonalInfoSelectOption,
} from "./PersonalInformationData";

type PersonalInfoSelectProps = {
    id: string;
    value: string;
    options: PersonalInfoSelectOption[];
    placeholder: string;
    disabled?: boolean;
    loading?: boolean;
    onChange: (value: string) => void;
};

const PersonalInfoSelect = ({
    id,
    value,
    options,
    placeholder,
    disabled = false,
    loading = false,
    onChange,
}: PersonalInfoSelectProps) => {
    const wrapperRef =
        useRef<HTMLDivElement>(
            null
        );

    const [
        open,
        setOpen,
    ] =
        useState(false);

    const [
        searchQuery,
        setSearchQuery,
    ] =
        useState("");

    const selectedOption =
        options.find(
            (
                option
            ) =>
                option.value ===
                value
        );

    const filteredOptions =
        options.filter(
            (opt) =>
                opt.label
                    .toLowerCase()
                    .includes(
                        searchQuery
                            .trim()
                            .toLowerCase()
                    ) ||
                opt.value
                    .toLowerCase()
                    .includes(
                        searchQuery
                            .trim()
                            .toLowerCase()
                    )
        );

    useEffect(
        () => {
            const handleOutside =
                (
                    event: MouseEvent
                ) => {
                    if (
                        wrapperRef.current &&
                        !wrapperRef.current.contains(
                            event.target as Node
                        )
                    ) {
                        setOpen(
                            false
                        );
                    }
                };

            const handleEscape =
                (
                    event: KeyboardEvent
                ) => {
                    if (
                        event.key ===
                        "Escape"
                    ) {
                        setOpen(
                            false
                        );
                    }
                };

            document.addEventListener(
                "mousedown",
                handleOutside
            );

            window.addEventListener(
                "keydown",
                handleEscape
            );

            return () => {
                document.removeEventListener(
                    "mousedown",
                    handleOutside
                );

                window.removeEventListener(
                    "keydown",
                    handleEscape
                );
            };
        },
        []
    );

    return (
        <div
            className="personal-info-select"
            ref={
                wrapperRef
            }
        >
            <button
                id={
                    id
                }
                type="button"
                className={`personal-info-select-trigger ${open
                        ? "open"
                        : ""
                    } ${disabled
                        ? "disabled"
                        : ""
                    }`}
                disabled={
                    disabled
                }
                onClick={() =>
                    setOpen(
                        (
                            previous
                        ) =>
                            !previous
                    )
                }
                aria-expanded={
                    open
                }
            >
                <span
                    className={
                        selectedOption
                            ? "select-value"
                            : "select-placeholder"
                    }
                >
                    {loading
                        ? "Loading..."
                        : selectedOption
                        ? selectedOption.label
                        : placeholder}
                </span>

                <ChevronDown
                    size={
                        16
                    }
                    strokeWidth={
                        2
                    }
                    className={
                        open
                            ? "rotate"
                            : ""
                    }
                />
            </button>

            {open && (
                <div
                    className="personal-info-select-menu"
                    role="listbox"
                >
                    {options.length > 7 && (
                        <div
                            style={{
                                padding: "4px 4px 6px",
                                position: "sticky",
                                top: 0,
                                background: "#ffffff",
                                zIndex: 2,
                            }}
                        >
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) =>
                                    setSearchQuery(
                                        e.target.value
                                    )
                                }
                                placeholder="Search..."
                                onClick={(e) =>
                                    e.stopPropagation()
                                }
                                style={{
                                    width: "100%",
                                    height: "32px",
                                    padding: "0 8px",
                                    fontSize: "11px",
                                    borderRadius: "6px",
                                    border: "1px solid #d2dbe6",
                                    outline: "none",
                                }}
                            />
                        </div>
                    )}

                    {filteredOptions.length >
                        0 ? (
                        filteredOptions.map(
                            (
                                option
                            ) => {
                                const selected =
                                    option.value ===
                                    value;

                                return (
                                    <button
                                        type="button"
                                        className={`personal-info-select-option ${selected
                                                ? "selected"
                                                : ""
                                            }`}
                                        key={
                                            option.value
                                        }
                                        onClick={() => {
                                            onChange(
                                                option.value
                                            );

                                            setOpen(
                                                false
                                            );
                                            setSearchQuery("");
                                        }}
                                    >
                                        <span>
                                            {
                                                option.label
                                            }
                                        </span>

                                        {selected && (
                                            <Check
                                                size={
                                                    15
                                                }
                                                strokeWidth={
                                                    2.5
                                                }
                                            />
                                        )}
                                    </button>
                                );
                            }
                        )
                    ) : (
                        <div className="personal-info-select-empty">
                            {options.length === 0 ? "No options available" : "No matching options"}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PersonalInfoSelect;
